'use client';
import { getApiBaseUrl } from "@/lib/apiBase";
import { useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";
import { MapPin, Phone, Mail, Globe, LayoutGrid, Rows, X, Star } from "lucide-react";
import Image from "next/image";
import { CategoryGlyph } from "@/components/CategoryGlyph";
const BASE_URL = getApiBaseUrl();

function displayCompanyName(p) {
  return p.company_name || p.companyName || p.name || "Компани";
}

function partnerCardImageUrl(p) {
  const raw = p.image || p.logoUrl;
  if (!raw) return null;
  if (String(raw).startsWith("http")) return raw;
  const base = BASE_URL || "";
  const path = String(raw).startsWith("/") ? raw : `/${raw}`;
  return base ? `${base}${path}` : path;
}

/** API: is_vip / vip нь ихэнхдээ isPaidListing-аас map хийгддэг */
function partnerIsVip(p) {
  return !!(p?.is_vip || p?.vip || p?.isVip || p?.isPaidListing);
}

export default function ParentCategoryPage() {
  const [tree, setTree] = useState({});
  const [openNodes, setOpenNodes] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [results, setResults] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams.get("search");

  const normalizeId = (value) =>
    value === null || typeof value === "undefined" ? "null" : String(value);

  // Fetch categories once
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await fetch(`/api/categories?all=1`);
        const data = await res.json();

        const parentMap = {};
        data.forEach((cat) => {
          const parentKey = normalizeId(cat.parent_id ?? null);
          if (!parentMap[parentKey]) parentMap[parentKey] = [];
          parentMap[parentKey].push(cat);
        });
        setTree(parentMap);
      } catch (err) {
        console.error("Fetch categories error:", err);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Expand nodes based on current query when tree is ready
  useEffect(() => {
    if (!query || Object.keys(tree).length === 0) return;

    const parentMap = tree;
    const targetKey = normalizeId(query);
    setSelectedId(targetKey);

    const openNodesTemp = {};
    const findParent = (idKey) => {
      for (const parentId in parentMap) {
        if (parentMap[parentId].some((c) => normalizeId(c.id) === normalizeId(idKey))) {
          if (parentId !== "null") {
            openNodesTemp[parentId] = true;
            findParent(parentId);
          }
        }
      }
    };

    findParent(targetKey);
    if ((parentMap[targetKey] || []).length > 0) {
      openNodesTemp[targetKey] = true;
    }

    setOpenNodes((prev) => ({ ...prev, ...openNodesTemp }));
  }, [query, tree]);

  // Fetch partner results whenever query changes (query байхгүй = бүх компани)
  useEffect(() => {
    const fetchResults = async () => {
      try {
        setResultsLoading(true);
        let data;
        if (!query || !String(query).trim()) {
          const res = await fetch(`/api/partner?type=partner`);
          data = await res.json();
        } else {
          const res = await fetch(
            `/api/partner?type=category&search=${encodeURIComponent(query)}`
          );
          data = await res.json();
        }
        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Search error:", err);
        setResults([]);
      } finally {
        setResultsLoading(false);
      }
    };

    fetchResults();
  }, [query]);

  const displayResults = useMemo(() => {
    const copy = [...results];
    copy.sort((a, b) => {
      const va = partnerIsVip(a);
      const vb = partnerIsVip(b);
      if (va !== vb) return va ? -1 : 1;
      return 0;
    });
    return copy;
  }, [results]);

  const toggleNode = (id) => {
    const key = normalizeId(id);
    setOpenNodes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClick = (node) => {
    const nodeKey = normalizeId(node.id);
    setSelectedId(nodeKey);
    if ((tree[nodeKey] || []).length > 0) {
      toggleNode(nodeKey);
    }
    router.replace(`/partner/partnercategory?search=${encodeURIComponent(node.id)}`);
  };

  const renderTree = (parentId = null, level = 0) => {
    const parentKey = normalizeId(parentId);
    return (tree[parentKey] || []).map((node) => {
      const nodeKey = normalizeId(node.id);
      const hasChildren = (tree[nodeKey] || []).length > 0;
      const isOpen = !!openNodes[nodeKey];
      const isSelected = normalizeId(selectedId) === nodeKey;

      return (
        <div key={node.id} className={`mb-1 ${level > 0 ? 'ml-4' : ''}`}>
            <div
              className={`flex items-center justify-between text-xs cursor-pointer px-3 py-2 rounded transition-colors
              ${isSelected
                ? "bg-[#577099] text-white shadow"
                : "text-gray-800 hover:bg-[#577099]/10 hover:text-[#577099]"
              }`}
            onClick={() => handleClick(node)}
          >
            <div className="flex items-center gap-2 flex-1 text-xs truncate whitespace-nowrap overflow-hidden">
              {hasChildren && (
                <span className={`${isSelected ? "text-white" : "text-gray-600"}`}>
                  {isOpen ? <FaChevronDown size={12}/> : <FaChevronRight size={12}/> }
                </span>
              )}
              <CategoryGlyph
                icon={node.icon}
                size="md"
                className={
                  isSelected
                    ? "!text-white [&_img]:brightness-0 [&_img]:invert"
                    : ""
                }
              />
              <span title={node.name} className={`truncate font-medium ${isSelected ? "text-white" : ""}`}>{node.name}</span>
              {node.companyCount !== undefined && node.companyCount > 0 && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                  isSelected 
                    ? "bg-white/20 text-white" 
                    : "bg-[#577099]/10 text-[#577099]"
                }`}>
                  ({node.companyCount})
                </span>
              )}
            </div>
          </div>

          {hasChildren && isOpen && (
            <div className="mt-1 pl-4 border-l border-gray-200">{renderTree(nodeKey, level + 1)}</div>
          )}
        </div>
      );
    });
  };

  if (categoriesLoading) {
    return (
      <div className="max-w-[1440px] mx-auto flex gap-6 p-4">
        <aside className="w-80 bg-white p-3 border border-gray-200 shadow-md rounded-lg h-[90vh] overflow-y-auto">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-24 mb-3"></div>
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded mb-2"></div>
            ))}
          </div>
        </aside>
        <main className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse bg-white shadow-md rounded-2xl p-6">
              <div className="w-36 h-36 bg-gray-200 rounded-lg mx-auto mb-4"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </main>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto flex gap-6 p-4">
      {/* Sidebar */}
      <aside className="w-80 bg-white p-3 border border-gray-200 shadow-md rounded-lg h-[90vh] overflow-y-auto">
        <h2 className="font-semibold text-gray-700 mb-3 text-sm">Ангилал</h2>
        {Object.keys(tree).length === 0 ? (
          <p className="text-xs text-gray-500 py-4">Ангилал ачаалагдаагүй эсвэл байхгүй.</p>
        ) : (
          renderTree(null)
        )}
      </aside>

      {/* Results */}
      <main className="flex-1">
        {/* Header with view mode */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-800">{results.length}</span>{" "}
              {query ? "компани (сонгосон ангилал + дэд ангиллууд)" : "компани (бүгд)"}
            </div>
            <div className="flex items-center gap-2">
                  <div className="flex rounded-2xl border border-gray-200 bg-white p-1 shadow-sm">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                        viewMode === "grid"
                          ? "bg-[#577099] text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <LayoutGrid className="h-4 w-4" />
                      Сүлжээ
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                        viewMode === "list"
                          ? "bg-[#577099] text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Rows className="h-4 w-4" />
                      Жагсаалт
                    </button>
                  </div>
            </div>
          </div>
        </div>

        {/* Partners Results */}
        {resultsLoading ? (
          <div className="space-y-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse bg-white rounded-3xl border border-gray-200 shadow-lg p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-28 h-28 md:w-40 md:h-40 bg-gray-200 rounded-2xl"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-6"}>
            {results.length === 0 ? (
              <div className="col-span-full">
                <p className="text-gray-500 text-center py-20">
                  {query
                    ? "Энэ ангилалд тохирох компани олдсонгүй."
                    : "Компани бүртгэгдээгүй байна."}
                </p>
              </div>
            ) : (
              displayResults.map((partner) => {
                const title = displayCompanyName(partner);
                const imgUrl = partnerCardImageUrl(partner);
                const isVIP = partnerIsVip(partner);
                const contactChips = [
                  partner.address && {
                    key: "address",
                    label: partner.address,
                    icon: <MapPin className="h-3.5 w-3.5" />,
                  },
                  partner.company_email && {
                    key: "email",
                    label: partner.company_email,
                    icon: <Mail className="h-3.5 w-3.5" />,
                  },
                  (partner.phone || partner.company_phone) && {
                    key: "phone",
                    label: partner.phone || partner.company_phone,
                    icon: <Phone className="h-3.5 w-3.5" />,
                  },
                  partner.company_website && {
                    key: "website",
                    label: partner.company_website,
                    icon: <Globe className="h-3.5 w-3.5" />,
                    href: partner.company_website,
                  },
                ].filter(Boolean);

                return (
                  <div
                    key={partner.id}
                    onClick={() => router.push(`/partner/${partner.id}`)}
                    className={`group relative overflow-hidden rounded-3xl bg-white shadow-lg transition-all duration-300 hover:shadow-2xl cursor-pointer ${
                      isVIP
                        ? "border-2 border-amber-300 shadow-amber-200/35 ring-1 ring-amber-200/60 hover:shadow-amber-200/50 hover:border-amber-400"
                        : "border border-gray-200 hover:shadow-[#577099]/20"
                    } ${viewMode === "list" ? "flex flex-col md:flex-row" : "flex flex-col"}`}
                  >
                    <div className={`relative flex gap-6 p-6 md:p-8 ${viewMode === "list" ? "flex-row w-full" : "flex-col"}`}>
                      <div
                        className={`relative ${viewMode === "list" ? "w-40 h-40 md:w-48 md:h-48" : "w-28 h-28 md:w-40 md:h-40 mx-auto md:mx-0"} flex-shrink-0 overflow-hidden rounded-2xl ring-2 ${
                          isVIP ? "ring-amber-300/90 shadow-md shadow-amber-200/40" : "ring-[#577099]/20"
                        }`}
                      >
                        {imgUrl ? (
                          <Image
                            src={imgUrl}
                            alt={title}
                            fill
                            className="object-cover"
                            unoptimized
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-[#577099] text-3xl font-semibold text-white/80">
                            {title?.[0] || "P"}
                          </div>
                        )}
                        {isVIP && (
                          <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border-2 border-white bg-gradient-to-r from-amber-400 to-yellow-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-lg sm:left-3 sm:top-3 sm:px-3 sm:py-1 sm:text-xs">
                            ⭐ VIP
                          </span>
                        )}
                      </div>

                      <div className="flex-1 flex flex-col gap-4">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#577099] transition-colors">
                                {title}
                              </h3>
                              {isVIP && (
                                <span className="hidden sm:inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-900">
                                  VIP
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-[#577099]" />
                              {partner.address || "Хаяг алга"}
                            </p>
                          </div>
                          <div className="md:text-right">
                            <PartnerCard partner={partner} router={router} />
                          </div>
                        </div>

                        {contactChips.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {contactChips.map((chip) =>
                              chip.href ? (
                                <a
                                  key={chip.key}
                                  href={chip.href}
                                  target="_blank"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-2 rounded-full border border-[#577099]/20 bg-[#577099]/5 px-3 py-1 text-xs font-medium text-[#577099] hover:border-[#577099]/30 hover:bg-[#577099]/10 transition-colors"
                                >
                                  {chip.icon}
                                  <span className="truncate max-w-[180px]">{chip.label}</span>
                                </a>
                              ) : (
                                <span
                                  key={chip.key}
                                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                                >
                                  {chip.icon}
                                  <span className="truncate max-w-[180px]">{chip.label}</span>
                                </span>
                              )
                            )}
                          </div>
                        )}

                        <div className="mt-auto flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              router.push(`/partner/${partner.id}`);
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-[#577099]/20 bg-[#577099]/5 px-4 py-2 text-sm font-medium text-[#577099] hover:border-[#577099]/30 hover:bg-[#577099]/10 transition-colors"
                          >
                            Профайл харах
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              router.push(`/review/${partner.id}`);
                            }}
                            className="inline-flex items-center gap-2 rounded-full bg-[#577099] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#4a5f85] transition-colors"
                          >
                            Сэтгэгдэл бичих
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>
    </div>
  );
}


function PartnerCard({ partner, router }) {
  const [isOpen, setIsOpen] = useState(false);
  const reviews = partner.reviews || [];
  const reviewCount = reviews.length;

  const validRatings = reviews.filter(r => r.rating > 0).map(r => r.rating);
  const avgRating =
    validRatings.length > 0
      ? validRatings.reduce((a, b) => a + b, 0) / validRatings.length
      : 0;

  const stars = Array.from({ length: 5 }, (_, i) => i + 1);

  return (
    <>
      {/* Rating display */}
      <div className="flex items-center gap-2 mt-2">
        <div className="flex relative">
          {stars.map((star, idx) => {
            const fill = Math.min(Math.max(avgRating - idx, 0), 1) * 100;
            return (
              <div key={idx} className="relative w-5 h-5">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-5 h-5 text-gray-300 absolute top-0 left-0"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.974a1 1 0 0 0 .95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.386 2.46a1 1 0 0 0-.364 1.118l1.287 3.974c.3.921-.755 1.688-1.54 1.118l-3.386-2.46a1 1 0 0 0-1.175 0l-3.386 2.46c-.784.57-1.838-.197-1.54-1.118l1.287-3.974a1 1 0 0 0-.364-1.118L2.045 9.4c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 0 0 .95-.69l1.286-3.974z" />
                </svg>
                <div
                  className="absolute top-0 left-0 h-full overflow-hidden"
                  style={{ width: `${fill}%` }}
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5 text-yellow-400"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.974a1 1 0 0 0 .95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.386 2.46a1 1 0 0 0-.364 1.118l1.287 3.974c.3.921-.755 1.688-1.54 1.118l-3.386-2.46a1 1 0 0 0-1.175 0l-3.386 2.46c-.784.57-1.838-.197-1.54-1.118l1.287-3.974a1 1 0 0 0-.364-1.118L2.045 9.4c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 0 0 .95-.69l1.286-3.974z" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        <span className="text-gray-800 font-medium">{avgRating.toFixed(1)}</span>

        {/* Review count button */}
        <button
          className="text-gray-500 text-sm underline hover:text-gray-700 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          ({reviewCount} сэтгэгдэл)
        </button>
      </div>

      {/* Reviews Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-10 backdrop-blur"
          onClick={() => setIsOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl"
          >
            <div className="relative bg-gradient-to-r from-[#1f2837] via-[#577099] to-[#2d3a4a] p-6 text-white">
              <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.1),_transparent)]" />
              <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_bottom,_rgba(255,255,255,0.05),_transparent)]" />
              <div className="relative flex flex-col gap-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/70">
                      Сэтгэгдлийн сан
                    </p>
                    <h3 className="text-2xl font-semibold leading-tight">
                      {displayCompanyName(partner)}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {reviewCount} хэрэглэгчийн үнэлгээ ба санал.
                    </p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="rounded-full border border-white/60 bg-white/30 p-2 text-white transition hover:bg-white/50"
                    aria-label="Close reviews dialog"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[480px] overflow-y-auto bg-white px-6 py-6 space-y-4">
              {reviews.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
                  <p className="text-gray-500">
                    Одоогоор сэтгэгдэл алга. Эхний сэтгэгдлийг үлдээгээрэй.
                  </p>
                  {router && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(false);
                        router.push(`/review/${partner.id}`);
                      }}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#577099] px-5 py-2 text-sm font-semibold text-white shadow hover:bg-[#4a5f85] transition-colors"
                    >
                      <Star className="h-4 w-4 text-white" />
                      Сэтгэгдэл нэмэх
                    </button>
                  )}
                </div>
              ) : (
                reviews.map(rev => (
                  <div
                    key={rev.id}
                    className="rounded-2xl border border-gray-100 bg-gray-50 p-5 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {rev.reviewer?.name || "Anonymous"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {rev.title || "Сэтгэгдэл"}
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-600">
                        <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
                        {rev.rating?.toFixed?.(1) || rev.rating}
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                      {rev.content}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}